// SAD-859: the work-product ledger's words and arithmetic, kept pure so the
// drawer, the team tab and the Beyond Scope page say the same thing.
//
// The vocabularies are the execution-scope enums. "Who asked" is five plain
// choices mapped onto ExecutionAuthorityBasis, with no new enum:
//
//   COR email           -> EMAIL_CONCURRENCE
//   Verbal request      -> MANUAL_ASSERTION   (only our word records it)
//   CO direction        -> WRITTEN_DIRECTION
//   Our own initiative  -> INTERNAL_PLAN
//   Other               -> UNKNOWN
//
// Defaults (the server applies the same ones): unsure unless a PWS section is
// named (then in scope); potential change when out of scope, else
// non-billable.
import { ExecutionAuthorityBasis as Basis, ExecutionCommercialTreatment as Commercial, ExecutionScopeAssessment as Scope } from '@mono/proto-clients';
export const WHO_ASKED = [
    {
        value: Basis.EMAIL_CONCURRENCE,
        label: 'COR email',
        hint: 'The COR asked or agreed in writing.'
    },
    {
        value: Basis.MANUAL_ASSERTION,
        label: 'Verbal request',
        hint: 'Asked for on a call or in person.'
    },
    {
        value: Basis.WRITTEN_DIRECTION,
        label: 'CO direction',
        hint: 'The Contracting Officer directed it.'
    },
    {
        value: Basis.INTERNAL_PLAN,
        label: 'Our own initiative',
        hint: 'Nobody asked; we chose to do it.'
    },
    {
        value: Basis.UNKNOWN,
        label: 'Other',
        hint: 'Say who in the notes.'
    }
];
export function whoAskedLabel(b) {
    return WHO_ASKED.find((w)=>w.value === b)?.label ?? humanize(Basis[b] ?? '');
}
export const SCOPE_BUCKETS = [
    {
        value: 'out',
        label: 'Out of scope',
        assessment: Scope.OUT_OF_SCOPE
    },
    {
        value: 'unsure',
        label: 'Unsure',
        assessment: Scope.UNKNOWN
    },
    {
        value: 'in',
        label: 'In scope',
        assessment: Scope.IN_SCOPE
    }
];
export function scopeBucket(s) {
    if (s === Scope.OUT_OF_SCOPE) return 'out';
    if (s === Scope.IN_SCOPE) return 'in';
    return 'unsure';
}
export function scopeLabel(s) {
    switch(s){
        case Scope.IN_SCOPE:
            return 'In scope';
        case Scope.OUT_OF_SCOPE:
            return 'Out of scope';
        case Scope.POTENTIAL_SCOPE_GROWTH:
            return 'Possible scope growth';
        case Scope.DISPUTED:
            return 'Disputed';
        default:
            return 'Unsure';
    }
}
export function scopeTone(s) {
    const b = scopeBucket(s);
    return b === 'out' ? 'danger' : b === 'in' ? 'success' : 'warning';
}
export function commercialLabel(c) {
    switch(c){
        case Commercial.POTENTIAL_CHANGE:
            return 'Potential change';
        case Commercial.NON_BILLABLE:
            return 'Non-billable';
        case Commercial.INCLUDED_IN_CLIN_PRICE:
            return 'Included in CLIN price';
        case Commercial.SEPARATELY_INVOICED:
            return 'Separately invoiced';
        default:
            return humanize(Commercial[c] ?? '');
    }
}
/** The assessment a new work product takes. */ export function defaultScope(pwsRef) {
    return pwsRef.trim() ? Scope.IN_SCOPE : Scope.UNKNOWN;
}
/** The treatment that follows an assessment. */ export function defaultCommercial(s) {
    return s === Scope.OUT_OF_SCOPE ? Commercial.POTENTIAL_CHANGE : Commercial.NON_BILLABLE;
}
function humanize(name) {
    const s = name.toLowerCase().replace(/_/g, ' ');
    return s ? s[0].toUpperCase() + s.slice(1) : 'Not stated';
}
/** "2026-03" from "2026-03-10". */ export function monthOf(producedOn) {
    return producedOn.slice(0, 7);
}
export function monthLabel(month) {
    const [y, m] = month.split('-').map(Number);
    if (!y || !m) return month;
    return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-US', {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC'
    });
}
export function filterWorkProducts(rows, f) {
    return rows.filter((r)=>(!f.scope || scopeBucket(r.scopeAssessment) === f.scope) && (!f.month || monthOf(r.producedOn) === f.month) && (!f.team || r.workstreamId === f.team));
}
export function totals(rows) {
    return rows.reduce((t, r)=>({
            count: t.count + 1,
            hours: t.hours + (r.hours ?? 0),
            valueCents: t.valueCents + Number(r.valueCents ?? 0)
        }), {
        count: 0,
        hours: 0,
        valueCents: 0
    });
}
export function formatHours(h) {
    return `${Number.isInteger(h) ? h : h.toFixed(1)} h`;
}
export function formatCents(c) {
    return (c / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: c % 100 === 0 ? 0 : 2
    });
}
/** "$1,250" -> 125000 cents; "" -> undefined; garbage -> NaN. */ export function parseDollars(s) {
    const t = s.replace(/[$,\s]/g, '');
    if (!t) return undefined;
    if (!/^\d+(\.\d{1,2})?$/.test(t)) return Number.NaN;
    return Math.round(Number(t) * 100);
}
/** "6.5" -> 6.5; "" -> undefined; garbage or negative -> NaN. */ export function parseHours(s) {
    const t = s.trim();
    if (!t) return undefined;
    if (!/^\d+(\.\d+)?$/.test(t)) return Number.NaN;
    return Number(t);
}
export const EXPORT_COLUMNS = [
    'Team',
    'Title',
    'Date produced',
    'Who asked',
    'PWS / Schedule B',
    'Scope',
    'Commercial treatment',
    'Hours',
    'Value (USD)',
    'Notes',
    'Logged by',
    'Evidence'
];
/**
 * One CSV cell. Free text a spreadsheet would run as a formula (a leading
 * =, +, -, @, tab or carriage return) is neutralized with a leading quote,
 * because the export is opened in Excel by whoever assembles the package.
 */ export function csvCell(v) {
    const safe = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
    return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}
export function workProductsCsv(rows, teamName, evidenceFor, personName = (id)=>id) {
    const lines = [
        EXPORT_COLUMNS.join(',')
    ];
    for (const r of rows){
        lines.push([
            teamName(r.workstreamId),
            r.title,
            r.producedOn,
            whoAskedLabel(r.authorityBasis),
            r.notInScopeDocs ? 'Not in PWS / Schedule B' : r.pwsRef,
            scopeLabel(r.scopeAssessment),
            commercialLabel(r.commercialTreatment),
            r.hours === undefined ? '' : String(r.hours),
            r.valueCents === undefined ? '' : (Number(r.valueCents) / 100).toFixed(2),
            r.description,
            personName(r.loggedByUserId),
            evidenceFor(r).map((e)=>e.reference).join(' | ')
        ].map(csvCell).join(','));
    }
    return `${lines.join('\r\n')}\r\n`;
}
/** A filename safe inside a ZIP. */ export function safeName(s) {
    const printable = [
        ...s
    ].map((c)=>c.charCodeAt(0) < 32 ? '_' : c).join('');
    return printable.replace(/[\\/:*?"<>|]+/g, '_').trim() || 'file';
}
